import { useId, useState } from "react";
import type { CatalogueEntry } from "../../catalogue/types";
import type { LayerSnapshot } from "../../core/store";
import { format } from "../../i18n/labels";
import { useIntl, useLabels, useMapCore, useMapStore } from "../hooks";
import { breadcrumb } from "../layerTreeModel";
import { Icon } from "../icons";
import { Button } from "../primitives/Button";
import { Chip } from "../primitives/Chip";
import { Slider } from "../primitives/Slider";
import { Legend } from "./Legend";
import { Swatch } from "./Swatch";

export type LayerRowProps = {
  snapshot: LayerSnapshot;
  entry?: CatalogueEntry | null;
  single?: boolean;
  onToggle?: (() => void) | null;
  onTable?: (() => void) | null;
  expandable?: boolean;
  className?: string;
};

export function zoomInRange(snapshot: Pick<LayerSnapshot, "minZoom" | "maxZoom">, zoom: number): boolean {
  if (snapshot.minZoom !== null && zoom < snapshot.minZoom) return false;
  if (snapshot.maxZoom !== null && zoom > snapshot.maxZoom) return false;

  return true;
}

export function LayerRow({ snapshot: given, entry = null, single = false, onToggle = null, onTable = null, expandable = true, className }: LayerRowProps) {
  const core = useMapCore();
  const labels = useLabels();
  const intl = useIntl();
  const zoom = useMapStore(a => a.view.zoom);
  const live = useMapStore(a => a.layers.find(b => b.id === given.id));
  const snapshot = live ?? given;
  const [expanded, setExpanded] = useState(false);
  const toolsId = useId();
  const inRange = zoomInRange(snapshot, zoom);
  const disabled = snapshot.disabledReason !== null;
  const hasZoomRange = snapshot.minZoom !== null || snapshot.maxZoom !== null;
  const title = entry !== null ? breadcrumb(entry) : snapshot.title;
  const legend = entry?.legend ?? null;

  const toggle = () => {
    if (disabled) return;
    if (onToggle !== null) onToggle();
    else core.layers.setSelected(snapshot.id, single ? true : !snapshot.selected);
  };

  const countText = snapshot.selected ? (snapshot.countLabel ?? (snapshot.count !== null ? intl.integer(snapshot.count) : null)) : null;

  return (
    <div
      className={[
        "fxmap-row",
        snapshot.selected ? "fxmap-row-on" : "",
        inRange ? "" : "fxmap-row-out",
        expanded ? "fxmap-row-expanded" : "",
        snapshot.status === "loading" ? "fxmap-row-loading" : "",
        snapshot.status === "error" ? "fxmap-row-error" : "",
        disabled ? "fxmap-row-disabled" : "",
        className ?? ""
      ]
        .filter(Boolean)
        .join(" ")}
      data-fxmap="layer-row"
      data-fxmap-layer={snapshot.id}
      title={title}
    >
      <div className="fxmap-row-main">
        {onTable !== null ? <Button kind="icon" size="sm" icon="grid" label={labels.layerTable} className="fxmap-row-table" action="layer-table" onClick={onTable} /> : null}
        <input className="fxmap-row-check" type={single ? "radio" : "checkbox"} checked={snapshot.selected} disabled={disabled} aria-label={snapshot.title} tabIndex={-1} onChange={toggle} onClick={event => event.stopPropagation()} />
        {entry !== null && (entry.kind === "tile" || entry.kind === "special") ? <Icon name="globe" className="fxmap-row-icon" /> : <Swatch entry={entry} snapshot={snapshot} />}
        <span className="fxmap-row-name-wrap" onClick={toggle}>
          <span className="fxmap-row-name fxmap-truncate">{snapshot.title}</span>
          {disabled ? <span className="fxmap-row-hint fxmap-muted">{snapshot.disabledReason}</span> : null}
          {!disabled && !inRange ? <span className="fxmap-row-hint fxmap-muted">{labels.zoomToSee}</span> : null}
          {!disabled && inRange && snapshot.truncated && snapshot.selected ? <span className="fxmap-row-hint fxmap-warning-text">{labels.truncated}</span> : null}
        </span>
        {hasZoomRange ? (
          <Chip tone="neutral" className="fxmap-row-zoom" title={format(labels.zoomRange, { min: snapshot.minZoom ?? 0, max: snapshot.maxZoom ?? core.options.view.maxZoom })}>
            {format(labels.zoomRange, { min: snapshot.minZoom ?? 0, max: snapshot.maxZoom ?? core.options.view.maxZoom })}
          </Chip>
        ) : null}
        {countText !== null ? <span className="fxmap-row-count fxmap-tabular fxmap-muted">{countText}</span> : null}
        {expandable ? <Button kind="icon" size="sm" icon={expanded ? "chevronUp" : "chevronDown"} label={labels.more} className="fxmap-row-expand" aria-expanded={expanded} aria-controls={toolsId} onClick={() => setExpanded(!expanded)} /> : null}
      </div>
      {snapshot.status === "loading" ? <span className="fxmap-row-progress" role="progressbar" aria-label={labels.loading} aria-valuetext={labels.loading} /> : null}
      {snapshot.status === "error" ? (
        <div className="fxmap-row-stripe fxmap-row-stripe-error" role="alert">
          <span className="fxmap-truncate">{snapshot.error ?? labels.loadFailed}</span>
          <Button kind="text" size="sm" label={labels.retry} action="retry" onClick={() => core.layers.refresh()} />
        </div>
      ) : null}
      {expandable && expanded ? (
        <div className="fxmap-row-tools" id={toolsId}>
          <Slider label={labels.opacity} showLabel value={Math.round(snapshot.opacity * 100)} min={0} max={100} valueText={value => `${value}%`} onChange={value => core.layers.setOpacity(snapshot.id, value / 100)} />
          <div className="fxmap-row-tool-actions">
            <Button kind="ghost" size="sm" icon="target" label={labels.onlyThis} action="solo" onClick={() => core.layers.solo(snapshot.id)} />
            <Button kind="ghost" size="sm" icon="fit" label={labels.zoomTo} action="zoom-to" onClick={() => core.layers.fit(snapshot.id)} />
          </div>
          {legend !== null && legend.length > 0 ? <Legend entries={legend} compact /> : null}
        </div>
      ) : null}
    </div>
  );
}
